import { useMemo } from 'react'
import { Layers } from 'lucide-react'
import { useGroupLookup } from '@/hooks/useLookup'
import { useTaskCategoryData } from '@/components/tasks/TaskCategorySelect/useTaskCategoryData'
import { cn } from '@/lib/utils'
import type { SnowflakeId } from '@/types/snowflake'

/** A resolved task-type token: a specific subtype, or a whole task-type category. */
export interface ResolvedTaskType {
  label: string
  /** true when the code is a bare type code (whole category, e.g. `maintenance`). */
  isWholeType: boolean
}

export interface ShiftMetaResolvers {
  loading: boolean
  /** group id → resort name (falls back to the raw id string when not found). */
  resolveGroupName: (id: SnowflakeId | string) => string
  /** subtype/type code → display label + whole-category flag. */
  resolveTaskType: (code: string) => ResolvedTaskType
}

/** Prettify an unknown code's last segment: `special.guest_comm` → "Guest comm". */
function prettifyCode(code: string): string {
  const last = code.split('.').pop() || code
  const spaced = last.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Resolvers backing the shift resort / task-type badges.
 *
 * - Resort names come from `useGroupLookup({ includeContainers: true })` (id → label「WIR · Windsor Island」).
 * - Task-type labels come from `useTaskCategoryData()` — a bare type code
 *   (no `.`) resolves to the type label (whole category); a dotted code
 *   resolves to the subtype label. Unknown codes fall back to a prettified
 *   last segment so display never shows a raw machine code.
 *
 * groupIds are handled as strings throughout (Snowflake ids) — never numeric.
 */
export function useShiftMeta(): ShiftMetaResolvers {
  const { data: groups, loading: groupsLoading } = useGroupLookup({ includeContainers: true })
  const { groups: categories, loading: catLoading } = useTaskCategoryData()

  return useMemo(() => {
    const groupMap = new Map<string, string>()
    for (const g of (groups ?? []) as Array<{ id: string; name: string; label?: string }>) {
      groupMap.set(String(g.id), g.label ?? g.name)
    }

    const typeMap = new Map<string, string>() // typeCode → typeLabel
    const subMap = new Map<string, string>() // dotted subtype code → subtype label
    for (const c of categories) {
      typeMap.set(c.typeCode, c.typeLabel)
      for (const s of c.subtypes) subMap.set(s.code, s.label)
    }

    return {
      loading: groupsLoading || catLoading,
      resolveGroupName: (id: SnowflakeId | string) => groupMap.get(String(id)) ?? String(id),
      resolveTaskType: (code: string): ResolvedTaskType => {
        const sub = subMap.get(code)
        if (sub) return { label: sub, isWholeType: false }
        const type = typeMap.get(code)
        if (type) return { label: type, isWholeType: true }
        // Unknown code: bare (no dot) → whole-category fallback; dotted → subtype fallback.
        return { label: prettifyCode(code), isWholeType: !code.includes('.') }
      },
    }
  }, [groups, categories, groupsLoading, catLoading])
}

// ─── Badge presentation (Tailwind semantic / status classes) ──────────────────

interface ResortBadgesProps {
  groupIds?: SnowflakeId[] | string[] | null
  resolveGroupName: (id: SnowflakeId | string) => string
  /** Cap the number of rendered badges; overflow collapses to "+N". */
  max?: number
  className?: string
}

/** Renders resort pills. Returns null when empty — callers decide the "All" copy. */
export function ResortBadges({ groupIds, resolveGroupName, max, className }: ResortBadgesProps) {
  if (!groupIds || groupIds.length === 0) return null
  const shown = max ? groupIds.slice(0, max) : groupIds
  const extra = groupIds.length - shown.length
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {shown.map((id) => (
        <span
          key={String(id)}
          className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium leading-none border border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-300"
        >
          {resolveGroupName(id)}
        </span>
      ))}
      {extra > 0 && (
        <span className="text-[11px] font-medium text-muted-foreground">+{extra}</span>
      )}
    </div>
  )
}

interface TaskTypeBadgesProps {
  codes?: string[] | null
  resolveTaskType: (code: string) => ResolvedTaskType
  /** Cap the number of rendered badges; overflow collapses to "+N". */
  max?: number
  className?: string
}

/**
 * Renders task-type pills. Whole-category tokens (bare type codes) get a Layers
 * glyph + a slightly stronger tint to signal they cover the entire category.
 * Returns null when empty — callers decide the "All" copy.
 */
export function TaskTypeBadges({ codes, resolveTaskType, max, className }: TaskTypeBadgesProps) {
  if (!codes || codes.length === 0) return null
  const shown = max ? codes.slice(0, max) : codes
  const extra = codes.length - shown.length
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {shown.map((code) => {
        const { label, isWholeType } = resolveTaskType(code)
        return (
          <span
            key={code}
            className={cn(
              'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] leading-none border',
              isWholeType
                ? 'font-semibold border-primary/30 bg-primary-soft text-primary'
                : 'font-medium border-primary/30 bg-primary-soft text-primary',
            )}
          >
            {isWholeType && <Layers className="h-2.5 w-2.5 shrink-0" />}
            {label}
          </span>
        )
      })}
      {extra > 0 && (
        <span className="text-[11px] font-medium text-muted-foreground">+{extra}</span>
      )}
    </div>
  )
}
